import React from "react";
import Modal from "@components/common/Modal/Modal";
import type { FilterModalConfig } from "../../types";
import CustomSelect from "@components/common/Select/Select";
import CustomMultiSelect from "@components/common/Select/MultiSelect";
import InputField from "@components/common/InputField";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@components/common/Button/Button";
import { FILTER_MODAL_SHELL } from "@config/theme/modals";

interface FilterModalProps {
	isOpen: boolean;
	onClose: () => void;
	config: FilterModalConfig;
	onApply: (filters: Record<string, unknown>) => void;
	isLoading?: boolean;
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, config, onApply, isLoading = false }) => {
	const [localFilters, setLocalFilters] = React.useState<Record<string, unknown>>({});
	const hasInitialized = React.useRef(false);

	// Initialize filters when modal opens
	React.useEffect(() => {
		// Initialize when modal opens (isOpen is true) and hasn't been initialized yet
		if (isOpen && !hasInitialized.current) {
			// Re-initialize local filters from config when modal opens
			const initialFilters: Record<string, unknown> = {};
			config.sections.forEach(section => {
				section.fields.forEach(field => {
					// Handle multiselect fields (arrays)
					if (field.type === "multiselect") {
						if (Array.isArray(field.value)) {
							initialFilters[field.name] = field.value;
						} else {
							initialFilters[field.name] = [];
						}
					} 
					// Handle other field types
					else if (field.value !== undefined && field.value !== null && field.value !== "") {
						initialFilters[field.name] = field.value;
					}
				});
			});
			setLocalFilters(initialFilters);
			hasInitialized.current = true;
		}
		
		// Reset initialization flag when modal closes
		if (!isOpen) {
			hasInitialized.current = false;
		}
	}, [config, isOpen]);

	const handleFieldChange = (fieldName: string, value: string | number | Date | null | string[], customOnChange?: (value: string | number | Date | null) => void) => {
		setLocalFilters(prev => {
			const newFilters = {
				...prev,
				[fieldName]: value,
			};
			
			// Clear city when district changes
			if (fieldName === "district") {
				newFilters.city = "";
			}

			// Call custom onChange immediately with the new value
			if (customOnChange && !Array.isArray(value)) {
				// Use setTimeout to ensure state update has been processed
				setTimeout(() => {
					customOnChange(value);
				}, 0);
			}

			return newFilters;
		});
	};

	const handleMultiSelectChange = (fieldName: string, selectedValues: string[]) => {
		setLocalFilters(prev => ({
			...prev,
			[fieldName]: selectedValues.length > 0 ? selectedValues : null,
		}));
	};

	const handleRemoveFilter = (fieldName: string) => {
		setLocalFilters(prev => {
			const newFilters = { ...prev };
			delete newFilters[fieldName];
			return newFilters;
		});
	};

	const handleApply = () => {
		onApply(localFilters);
		onClose();
	};

	// Get applied filters for display
	const getAppliedFilters = () => {
		const applied: Array<{ name: string; label: string; value: string }> = [];

		config.sections.forEach(section => {
			section.fields.forEach(field => {
				const value = localFilters[field.name];
				
				// Handle multiselect (array)
				if (Array.isArray(value)) {
					// Strictly filter out empty values before checking length
					const validValues = value.filter(v => {
						if (v === null || v === undefined || v === "") return false;
						if (typeof v === 'string' && v.trim() === "") return false;
						return true;
					});
					
					// Only show filter chip if there are valid selected values
					if (validValues.length > 0) {
						const labels = validValues
							.map(val => {
								if (field.options) {
									const option = field.options.find(opt => String(opt.value) === String(val));
									return option ? option.label : val;
								}
								return String(val);
							})
							.filter(Boolean);
					
						if (labels.length > 0) {
							applied.push({
								name: field.name,
								label: field.label,
								value: labels.join(", "),
							});
						}
					}
				} else if (value && value !== "" && value !== null && value !== undefined) {
					// Handle single select/text - skip placeholder values
					let displayValue = String(value);

					// Only try to get the label from options for select fields (not text fields)
					// Text fields (like location) should use the entered value directly
					if (field.type === "select" && field.options) {
						const option = field.options.find(opt => String(opt.value) === String(value));
						if (option) {
							// Don't show if it's a placeholder option (like "All Districts", "Select District", etc.)
							if (!option.label.toLowerCase().includes("all ") && 
							    !option.label.toLowerCase().includes("select ") &&
								option.value !== "") {
								displayValue = option.label;
							} else {
								// Skip placeholder values
								return;
							}
						}
					}
					// For text fields, use the value as-is (no option lookup)

					applied.push({
						name: field.name,
						label: field.label,
						value: displayValue,
					});
				}
			});
		});

		return applied;
	};

	const renderField = (field: FilterModalConfig["sections"][0]["fields"][0]) => {
		const value = localFilters[field.name] || "";

		switch (field.type) {
			case "select": {
				// Convert options to CustomSelect format
				// Filter out empty values to avoid duplicate keys with placeholder
				const validOptions = (field.options || [])
					.filter(option => option.value !== undefined && option.value !== null && option.value !== "")
					.map(option => ({
						value: String(option.value), // Ensure value is a string
						label: option.label,
						name: option.label, // Add name property for MenuItem display
					}));
				
				const selectOptions = [
					{ value: "", label: field.placeholder || `Select ${field.label}`, name: field.placeholder || `Select ${field.label}`, isPlaceholder: true },
					...validOptions,
				];

				// Ensure the selected value is a string
				const selectedValue = value !== undefined && value !== null && value !== "" ? String(value) : "";

				return (
					<CustomSelect
						options={selectOptions}
						selected={selectedValue}
						setSelected={(val: string | number) => {
							// Convert to string and ensure it's not empty (unless it's explicitly clearing)
							const stringVal = val !== null && val !== undefined ? String(val) : "";
							handleFieldChange(field.name, stringVal === "" ? "" : stringVal, field.onChange);
						}}
						placeholder={field.placeholder || `Select ${field.label}`}
						id={`filter-${field.name}`}
						disabled={isLoading || field.disabled}
						hasNextPage={field.hasNextPage}
						onLoadMore={field.onLoadMore}
						isLoadingMore={field.isLoadingMore}
					/>
				);
			}
			case "date":
				return (
					<input
						type="date"
						value={value instanceof Date ? value.toISOString().split("T")[0] : String(value)}
						onChange={e => handleFieldChange(field.name, e.target.value, field.onChange)}
						className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
						disabled={isLoading}
					/>
				);
			case "text":
				return (
					<InputField
						type="text"
						value={String(value)}
						onChange={e => handleFieldChange(field.name, e.target.value, field.onChange)}
						placeholder={field.placeholder}
						disabled={isLoading}
						size="medium"
						label="" // Empty label since we're already showing label above
					/>
				);
			case "multiselect": {
				// Ensure we have an array and strictly filter out any empty/null/undefined values
				const rawValues = Array.isArray(value) ? value : [];
				const selectedValues = rawValues
					.filter(v => {
						if (v === null || v === undefined || v === "") return false;
						if (typeof v === 'string' && v.trim() === "") return false;
						return true;
					})
					.map(v => String(v)); // Ensure all values are strings
				
				const multiSelectOptions = (field.options || []).map(option => ({
					value: String(option.value),
					label: option.label,
				}));

				return (
					<CustomMultiSelect
						options={multiSelectOptions}
						selected={selectedValues.length > 0 ? selectedValues : []}
						setSelected={(vals: string[]) => handleMultiSelectChange(field.name, vals)}
						placeholder={field.placeholder || `Select ${field.label}`}
						id={`filter-${field.name}`}
						showSearch={true}
						maxHeight={200}
						hasNextPage={field.hasNextPage}
						onLoadMore={field.onLoadMore}
						isLoadingMore={field.isLoadingMore}
					/>
				);
			}
			default:
				return null;
		}
	};

	const appliedFilters = getAppliedFilters();

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={config.title || "Filters"}
			width={FILTER_MODAL_SHELL.width}
			height={FILTER_MODAL_SHELL.height}
			rounded={FILTER_MODAL_SHELL.rounded}
			padding={FILTER_MODAL_SHELL.padding}
			zIndex={FILTER_MODAL_SHELL.zIndex}
			footer={
				<div className="flex justify-end border-t border-gray-200 p-6 pt-4">
					<Button
						onClick={handleApply}
						type="submit"
						disabled={isLoading}
						variant="primary"
						text={isLoading ? "Applying..." : "Apply Filters"}
						className="min-w-[150px]"
					/>
				</div>
			}>
			{appliedFilters?.length > 0 && (
				<div className="border-b border-gray-200 p-6 pb-4">
					<div className="flex flex-wrap gap-2">
						{appliedFilters?.map(filter => (
							<div
								key={filter.name}
								className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary-soft px-3 py-2 text-sm text-primary-soft-text">
								<span>{filter.value}</span>
								<button
									onClick={() => handleRemoveFilter(filter.name)}
									className="text-primary transition-colors hover:text-primary-soft-text"
									aria-label={`Remove ${filter.label} filter`}
									type="button">
									<CloseIcon fontSize="small" />
								</button>
							</div>
						))}
					</div>
				</div>
			)}

			{config?.sections?.map((section, sectionIndex) => (
				<div key={sectionIndex} className="p-6 pb-4">
					{section.title && <h3 className="mb-4 text-lg font-medium text-foreground">{section.title}</h3>}
					<div className={`grid gap-4 ${section.fields?.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
						{section.fields?.map((field, fieldIndex) => (
							<div key={fieldIndex} className="space-y-2">
								<label className="block text-sm font-medium text-gray-700">{field.label}</label>
								{renderField(field)}
							</div>
						))}
					</div>
				</div>
			))}
		</Modal>
	);
};

export default FilterModal;
